// client/src/services/donationsApi.js
// Donations (Roadmap Phase 5.1). Staff: /api/donations/:tenant (checked
// against the session, server/routes/donationRoutes.js). Public donation
// pages: /api/public/donations (no sign-in).

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';

async function request(method, url, body) {
  const res = await fetch(`${BASE_URL}${url}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || `${res.status} ${res.statusText}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

const base = (tenantId) => `/api/donations/${Number(tenantId)}`;
const pub = '/api/public/donations';
const enc = encodeURIComponent;

export function errorText(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

/** Cents → "$25" / "$25.50" in the page currency. */
export function formatMoney(cents, currency = 'usd') {
  const v = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: String(currency || 'usd').toUpperCase(),
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2,
    }).format(v);
  } catch { return `$${v.toFixed(2)}`; }
}

/** Same estimate as the server (services/donations.js feeCover). */
export function feeCover(amountCents, coverFees) {
  const a = Math.max(0, Math.round(Number(amountCents) || 0));
  const pct = Math.max(0, Number(coverFees?.percent) || 0) / 100;
  const fixed = Math.max(0, Math.round(Number(coverFees?.fixedCents) || 0));
  if (!a || pct >= 1) return 0;
  return Math.ceil((a + fixed) / (1 - pct)) - a;
}

// ---- staff
export const getDonationSettings = (tenantId) => request('GET', `${base(tenantId)}/settings`);
export const saveDonationSettings = (tenantId, body) => request('PUT', `${base(tenantId)}/settings`, body);
export const listDonationPages = (tenantId) => request('GET', `${base(tenantId)}/pages`);
export const createDonationPage = (tenantId, body) => request('POST', `${base(tenantId)}/pages`, body);
export const updateDonationPage = (tenantId, id, body) => request('PUT', `${base(tenantId)}/pages/${Number(id)}`, body);
export const deleteDonationPage = (tenantId, id) => request('DELETE', `${base(tenantId)}/pages/${Number(id)}`);
export function listDonations(tenantId, { pageId, status, q, before } = {}) {
  const qs = new URLSearchParams();
  if (pageId) qs.set('pageId', pageId);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  if (before) qs.set('before', before);
  const s = qs.toString();
  return request('GET', `${base(tenantId)}/donations${s ? `?${s}` : ''}`);
}
export const refundDonation = (tenantId, id, amountCents) => request('POST', `${base(tenantId)}/donations/${Number(id)}/refund`, amountCents ? { amountCents } : {});
export const resendReceipt = (tenantId, id) => request('POST', `${base(tenantId)}/donations/${Number(id)}/receipt`);
export const listMonthlyDonors = (tenantId) => request('GET', `${base(tenantId)}/monthly`);
export const cancelMonthlyGift = (tenantId, id) => request('POST', `${base(tenantId)}/monthly/${Number(id)}/cancel`);
export function getGiving(tenantId, { email, memberId } = {}) {
  const qs = new URLSearchParams();
  if (email) qs.set('email', email);
  if (memberId) qs.set('memberId', memberId);
  return request('GET', `${base(tenantId)}/giving?${qs.toString()}`);
}
export const listYearSummaries = (tenantId, year) => request('GET', `${base(tenantId)}/summaries?year=${Number(year)}`);
export const previewYearSummary = (tenantId, year, email) => request('GET', `${base(tenantId)}/summaries/preview?year=${Number(year)}&email=${enc(email)}`);
export const sendYearSummaries = (tenantId, year, emails) => request('POST', `${base(tenantId)}/summaries/send`, { year: Number(year), emails: emails && emails.length ? emails : undefined });

// ---- public
export const getPublicDonationPage = (tenantSlug, pageSlug) => request('GET', `${pub}/${enc(tenantSlug)}/${enc(pageSlug)}`);
export const startGift = (tenantSlug, pageSlug, body) => request('POST', `${pub}/${enc(tenantSlug)}/${enc(pageSlug)}/intent`, body);
export const confirmGift = (tenantSlug, pageSlug, body) => request('POST', `${pub}/${enc(tenantSlug)}/${enc(pageSlug)}/confirm`, body);
export const getManagedGift = (token) => request('GET', `${pub}/manage/${enc(token)}`);
export const cancelManagedGift = (token) => request('POST', `${pub}/manage/${enc(token)}/cancel`);
